import { Fragment } from 'react';
import {
  type EventQualityKey26Co,
  useFeedback26CoStore,
} from '@/stores/feedback-26-co';
import { useFeedbackEdition } from '../edition-context';

const RATING_GROUPS = [
  {
    title: 'Infraestructura',
    items: ['oficina', 'wifi', 'comida'] as const,
  },
  {
    title: 'Software & Comunicación',
    items: ['software', 'comunicacion', 'branding'] as const,
  },
  {
    title: 'Personas',
    items: ['mentores', 'jueces', 'sponsors', 'nivelTecnico'] as const,
  },
  {
    title: 'Competencia',
    items: [
      'tracks',
      'premios',
      'procesoEvaluacion',
      'organizacion',
      'platanusHackMarket',
    ] as const,
  },
];

function PlatanusIcon({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block bg-current ${className || ''}`.trim()}
      style={{
        maskImage: 'url(/assets/logos/platanus-isotype.svg)',
        WebkitMaskImage: 'url(/assets/logos/platanus-isotype.svg)',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
      }}
    />
  );
}

function BananaRating({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((banana) => (
        <button
          key={banana}
          type="button"
          onClick={() => onChange(banana)}
          className={`text-2xl transition-colors ${
            banana <= value
              ? 'text-primary'
              : 'text-muted-foreground/30 hover:text-primary/50'
          }`}
          aria-label={`${banana} plátanos`}
        >
          <PlatanusIcon className="h-6 w-6" />
        </button>
      ))}
    </div>
  );
}

// The labels are the edition's and arrive as a prop (FeedbackEditions, PORTING.md G5);
// only the grouping is the component's.
export function EventQuality({
  qualityLabels,
}: {
  qualityLabels: Record<string, string>;
}) {
  const { formData, setEventQualityRating, setHackRacerRating } =
    useFeedback26CoStore();
  const { asksHackRacer } = useFeedbackEdition();

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 font-bold text-2xl">Calidad del evento</h2>
        <p className="text-muted-foreground">
          Califica los siguientes aspectos del evento (1-5 plátanos).
        </p>
      </div>

      {RATING_GROUPS.map((group) => (
        <div key={group.title} className="space-y-4">
          <h3 className="font-semibold text-lg text-primary">{group.title}</h3>
          <div className="space-y-3">
            {group.items.map((item) => (
              <Fragment key={item}>
                {/* The Hack Racer is its own column, not a quality dimension, but it
                    is rated right before the Market. */}
                {item === 'platanusHackMarket' && asksHackRacer && (
                  <div className="flex flex-col gap-2 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between">
                    <span className="font-medium">Platanus Hack Racer</span>
                    <BananaRating
                      value={formData.hackRacerRating ?? 0}
                      onChange={setHackRacerRating}
                    />
                  </div>
                )}
                <div className="flex flex-col gap-2 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between">
                  <span className="font-medium">{qualityLabels[item]}</span>
                  <BananaRating
                    value={
                      formData.eventQualityRatings[item as EventQualityKey26Co]
                    }
                    onChange={(value) =>
                      setEventQualityRating(item as EventQualityKey26Co, value)
                    }
                  />
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
